import { FunctionComponent } from "react";
import { Button, Divider, Space, Table, Tag, Tooltip, Typography, theme } from "antd";
import {
    CheckCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    FilterOutlined,
    LockOutlined,
    MailOutlined,
    PlusOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import { UtilisateurViewProps } from "./types";
import { UserDto, RoleDto } from "../../../services/user";
import { ColumnsType } from "antd/es/table";
import { getRoleLibelle, getRolesOptions } from "../../../components/common/commoninputs";
import { AdminSearchFilter, InputSearchFieldDef } from "../../../components/common/AdminSearchFilter";
import { AdminFilterPanel } from "../../../components/common/AdminFilterPanel";

const filtresUtilisateur: InputSearchFieldDef[] = [
    { name: "prenomFilter", libelle: "Prénom", inputType: "InputText" },
    { name: "nomFilter", libelle: "Nom", inputType: "InputText" },
    { name: "emailFilter", libelle: "E-mail", inputType: "InputText" },
    { name: "roleFilter", libelle: "Rôle", inputType: "Select", selectOptions: getRolesOptions() },
];

/** Génère les initiales prénom + nom (max 2 caractères). */
const getInitials = (prenom: string, nom: string): string => {
    const p = (prenom ?? "").trim()[0] ?? "";
    const n = (nom ?? "").trim()[0] ?? "";
    return (p + n).toUpperCase();
};

/**
 * Palette de couleurs d'avatar déterministe basée sur le username.
 * Évite le violet #597ef7 pour ne pas confondre avec les éléments interactifs.
 */
const AVATAR_COLORS = [
    "#2f54eb", "#13c2c2", "#52c41a", "#fa8c16",
    "#eb2f96", "#722ed1", "#1890ff", "#fa541c",
];

const getAvatarColor = (seed: string): string => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const UtilisateurDesktopView: FunctionComponent<UtilisateurViewProps> = ({
    form,
    users,
    onCreateUser,
    onEditUser,
    onDeleteUser,
    onResendActivationMail,
    fetchUsers,
}) => {
    const { token } = theme.useToken();

    /** Retrait d'un critère depuis les puces du bandeau replié. */
    const clearFilter = (name: string) => {
        form.setFieldValue(name, undefined);
        fetchUsers();
    };

    /* ── Stats ── */
    const totalUsers = users?.length ?? 0;
    const activeUsers = users?.filter((u) => u.enabled).length ?? 0;
    const lockedUsers = users?.filter((u) => u.locked).length ?? 0;

    const columns: ColumnsType<UserDto> = [
        {
            title: "Utilisateur",
            dataIndex: "nom",
            key: "nom",
            sorter: (a, b) => a.nom.localeCompare(b.nom),
            render: (_: string, record: UserDto) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {/* Avatar initiales */}
                    <div
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: getAvatarColor(record.username),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: 13,
                            letterSpacing: 0.5,
                            userSelect: "none",
                        }}
                        aria-hidden="true"
                    >
                        {getInitials(record.prenom, record.nom)}
                    </div>
                    <div>
                        <Typography.Text strong style={{ display: "block", lineHeight: 1.4 }}>
                            {record.nom} {record.prenom}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {record.username}
                        </Typography.Text>
                    </div>
                </div>
            ),
        },
        {
            title: "Contact",
            key: "contact",
            render: (_: any, record: UserDto) => (
                <div>
                    <Typography.Text style={{ display: "block", fontSize: 13 }}>{record.email}</Typography.Text>
                    {record.mobile && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.mobile}</Typography.Text>
                    )}
                </div>
            ),
        },
        {
            title: "Rôles",
            dataIndex: "roles",
            key: "roles",
            render: (roles: RoleDto[]) => (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {roles.map((r) => (
                        <Tag key={r.role} color="geekblue" style={{ borderRadius: 4 }}>
                            {getRoleLibelle(r)}
                        </Tag>
                    ))}
                </div>
            ),
        },
        {
            title: "Statut",
            key: "statut",
            width: 160,
            render: (_: any, record: UserDto) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {record.enabled ? (
                        <Tag color="success" style={{ margin: 0, width: "fit-content" }}>Actif</Tag>
                    ) : (
                        <Tag color="error" style={{ margin: 0, width: "fit-content" }}>Inactif</Tag>
                    )}
                    {record.locked && (
                        <Tag
                            color="error"
                            icon={<LockOutlined />}
                            style={{ margin: 0, width: "fit-content" }}
                        >
                            Verrouillé
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, user: UserDto) => (
                <Space size={4}>
                    <Tooltip title="Modifier">
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            style={{ color: "#597ef7", borderColor: "#597ef7" }}
                            onClick={() => onEditUser(user)}
                            {...({ testid: `modifier-utilisateur-${user.id}` } as any)}
                        />
                    </Tooltip>
                    <Tooltip title={user.enabled ? "Compte déjà activé" : "Renvoyer le mail d'activation"}>
                        <Button
                            icon={<MailOutlined />}
                            size="small"
                            style={{ color: user.enabled || !user.email ? "#d9d9d9" : "#fa8c16", borderColor: user.enabled || !user.email ? "#d9d9d9" : "#fa8c16" }}
                            disabled={user.enabled || !user.email}
                            onClick={() => onResendActivationMail(user)}
                            {...({ testid: `renvoyer-activation-utilisateur-${user.id}` } as any)}
                        />
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                            onClick={() => onDeleteUser(user)}
                            {...({ testid: `supprimer-utilisateur-${user.id}` } as any)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* ── Bandeau filtres, pleine largeur ── */}
            <AdminFilterPanel
                form={form}
                filterDefs={filtresUtilisateur}
                onClearFilter={clearFilter}
            >
                <AdminSearchFilter layout="grid" doSearch={fetchUsers} inputFilters={filtresUtilisateur} />
            </AdminFilterPanel>

            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
                {/* ── Stats bar ── */}
                <div className="utilisateur-stats-bar">
                    <div className="utilisateur-stat-item">
                        <TeamOutlined className="utilisateur-stat-icon utilisateur-stat-icon--total" />
                        <div>
                            <span className="utilisateur-stat-value">{totalUsers}</span>
                            <span className="utilisateur-stat-label">
                                utilisateur{totalUsers > 1 ? "s" : ""}
                            </span>
                        </div>
                    </div>
                    <div className="utilisateur-stat-divider" />
                    <div className="utilisateur-stat-item">
                        <CheckCircleOutlined className="utilisateur-stat-icon utilisateur-stat-icon--active" />
                        <div>
                            <span className="utilisateur-stat-value utilisateur-stat-value--active">{activeUsers}</span>
                            <span className="utilisateur-stat-label">actif{activeUsers > 1 ? "s" : ""}</span>
                        </div>
                    </div>
                    <div className="utilisateur-stat-divider" />
                    <div className="utilisateur-stat-item">
                        <LockOutlined className="utilisateur-stat-icon utilisateur-stat-icon--locked" />
                        <div>
                            <span className="utilisateur-stat-value utilisateur-stat-value--locked">{lockedUsers}</span>
                            <span className="utilisateur-stat-label">verrouillé{lockedUsers > 1 ? "s" : ""}</span>
                        </div>
                    </div>
                    <div style={{ flex: 1 }} />
                    <Button
                        type="primary"
                        onClick={onCreateUser}
                        icon={<PlusOutlined />}
                        style={{ backgroundColor: "#597ef7", borderColor: "#597ef7" }}
                    >
                        Nouvel utilisateur
                    </Button>
                </div>

                {/* ── Zone résultats ── */}
                {users && users.length > 0 ? (
                    <div
                        style={{
                            background: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `1px solid ${token.colorBorder}`,
                            overflow: "hidden",
                            boxShadow: token.boxShadowSecondary,
                        }}
                    >
                        <div style={{ padding: "0 0 8px 0" }}>
                            <Table<UserDto>
                                dataSource={users}
                                columns={columns}
                                rowKey={(record) => record.username}
                                size="middle"
                                scroll={{ x: "max-content" }}
                                pagination={{
                                    showSizeChanger: true,
                                    defaultPageSize: 10,
                                    pageSizeOptions: ["10", "20", "50"],
                                    showTotal: (total, range) => (
                                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                                            {range[0]}–{range[1]} sur <strong>{total}</strong> résultat(s)
                                        </Typography.Text>
                                    ),
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "48px 24px",
                            background: "#fff",
                            borderRadius: 12,
                            border: "1px dashed #d9d9d9",
                            gap: 8,
                        }}
                        role="status"
                        aria-live="polite"
                    >
                        <FilterOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
                        <Typography.Text style={{ color: "#8c8c8c", fontSize: 15 }}>
                            Aucun utilisateur ne correspond aux filtres
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                            Modifiez les critères de recherche ci-dessus pour affiner les résultats.
                        </Typography.Text>
                    </div>
                )}
            </div>
        </div>
    );
};
